/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import _get from "lodash/get";
import _findKey from "lodash/findKey";
import formStepsAlimony from "./alimony/formSteps";
import FormServiceAlimony from "./alimony/FormService";
import formStepsDivorce from "./divorce/formSteps";
import FormServiceDivorce from "./divorce/FormService";
import formStepsDeath from "./death/formSteps";
import FormServiceDeath from "./death/FormService";
import formStepsContractLaw from "./contractLaw/formSteps";
import formStepsRentalContract from "./rentalContract/formSteps";
import formStepsDataLeakContract from "./contractLaw/dataLeakContract/formSteps";
import FormServiceContractLaw from "./contractLaw/FormService";
import FormServiceRentalContract from "./rentalContract/FormService";
import FormServiceDataLeakContract from "./contractLaw/dataLeakContract/FormService";
import formStepsProcessCostSupport from "./processCostSupport/formSteps";
import FormServiceProcessCostSupport from "./processCostSupport/FormService";
import formStepsFamily from "./familyLaw/formSteps";
import FormServiceFamily from "./familyLaw/FormService";
import formStepsSettlement from "./settlement/formSteps";
import FormServiceSettlement from "./settlement/FormService";
import formStepsTraffic from "./traffic/formSteps";
import FormServiceTraffic from "./traffic/FormService";
import formStepsTrafficAccident from "./trafficAccident/formSteps";
import FormServiceTrafficAccident from "./trafficAccident/FormService";
import formStepsGeneralCivilLaw from "./generalCivilLaw/formSteps";
import FormServiceGeneralCivilLaw from "./generalCivilLaw/FormService";
import formStepsEmploymentContract from "./employmentContract/formSteps";
import FormServiceEmploymentContract from "./employmentContract/FormService";
import formStepsCivilServiceLaw from "./civilServiceLaw/formSteps";
import FormServiceCivilServiceLaw from "./civilServiceLaw/FormService";
import { APPLICATION_STATUS_SUMMARY as APPLICATION_STATUS_SUMMARY_ALIMONY } from "./alimony/applicationStatus";
import { APPLICATION_STATUS_SUMMARY as APPLICATION_STATUS_SUMMARY_CIVIL_SERVICE_LAW } from "./civilServiceLaw/applicationStatus";
import { APPLICATION_STATUS_SUMMARY as APPLICATION_STATUS_SUMMARY_DIVORCE } from "./divorce/applicationStatus";
import { APPLICATION_STATUS_SUMMARY as APPLICATION_STATUS_SUMMARY_DEATH } from "./death/applicationStatus";
import { APPLICATION_STATUS_SUMMARY as APPLICATION_STATUS_SUMMARY_CONTRACT } from "./contractLaw/applicationStatus";
import { APPLICATION_STATUS_SUMMARY as APPLICATION_STATUS_SUMMARY_SETTLEMENT } from "./settlement/applicationStatus";
import { APPLICATION_STATUS_SUMMARY as APPLICATION_STATUS_SUMMARY_TRAFFIC } from "./traffic/applicationStatus";
import { APPLICATION_STATUS_SUMMARY as APPLICATION_STATUS_SUMMARY_TRAFFIC_ACCIDENT } from "./trafficAccident/applicationStatus";
import { APPLICATION_STATUS_SUMMARY as APPLICATION_STATUS_SUMMARY_PCS } from "./processCostSupport/applicationStatus";
import { APPLICATION_STATUS_SUMMARY as APPLICATION_STATUS_SUMMARY_GENERAL_CIVIL_LAW } from "./generalCivilLaw/applicationStatus";
import { APPLICATION_STATUS_SUMMARY as APPLICATION_STATUS_SUMMARY_EMPLOYMENT_CONTRACT } from "./employmentContract/applicationStatus";
import iconContract from "@/src/assets/icons/serviceOverview/IconContract";
import iconFamily from "@/src/assets/icons/familyLaw/IconFamily";
import iconTraffic from "@/src/assets/icons/traffic/IconTrafficLawMinimal";
import iconEmploymentLaw from "@/src/assets/icons/generic/IconSuitCase";
import iconSettlement from "@/src/assets/icons/generic/IconSuitCase";
import iconTenancyLaw from "@/src/assets/icons/tenancyLaw/IconDocumentHouseSign";
import iconDivorce from "@/src/assets/icons/familyLaw/IconSignpost";
import iconAlimony from "@/src/assets/icons/familyLaw/IconBooking";
import iconDeath from "@/src/assets/icons/settlement/iconAttorneyCheck";
import iconDataLeakContract from "@/src/assets/icons/serviceOverview/IconDocumentDataleak";
import iconShortTimeWork from "@/src/assets/icons/shortTimeWork/IconShortTimeWork";
import iconTrafficAccident from "@/src/assets/icons/trafficLaw/toggleable/IconTrafficAccident";
import iconCourtScales from "@/src/assets/icons/serviceOverview/IconCourtScales";
import iconEmploymentContract from "@/src/assets/icons/generic/IconPeople";
import iconCivilServiceLaw from "@/src/assets/icons/administrativeLaw/IconCivilServiceLaw";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { FormServiceBase, FormSteps } from "@/src/types/FormServiceBase";
import React from "react";
import { isPartnerIriOneOf, PARTNER_IRIS } from "@/src/constants/partnerIris";

export const productCategoryData = {
  administrativeLaw: {
    label: "Verwaltungsrecht",
    icon: iconCivilServiceLaw,
  },
  contractLaw: {
    label: "Vertragsrecht",
    icon: iconContract,
  },
  familyLaw: {
    label: "Familienrecht",
    icon: iconFamily,
  },
  trafficLaw: {
    label: "Verkehrsrecht",
    icon: iconTraffic,
  },
  none: {
    label: "Neuer Fall",
    icon: iconContract,
  },
  civilLaw: {
    label: "Zivilrecht",
    icon: iconCourtScales,
  },
  employmentLaw: {
    label: "Arbeitsrecht",
    icon: iconEmploymentLaw,
  },
  tenancyLaw: {
    label: "Mietrecht",
    icon: iconTenancyLaw,
  },
};

const productData: Record<
  AbstractCase["name"],
  {
    urlPart?: string;
    urlPartBase?: string;
    urlPartShort: string;
    urlPartProductCategory: string;
    label?: string;
    apiUri: string;
    formSteps?: FormSteps;
    formService?: FormServiceBase<any>;
    summaryStatus?: number;
    productCategory?: (typeof productCategoryData)[keyof typeof productCategoryData];
    productIcon?: React.FC;
  }
> = {
  alimony: {
    urlPart: "familienrecht/unterhalt",
    urlPartBase: "familienrecht/unterhalt",
    urlPartShort: "unterhalt",
    urlPartProductCategory: "familienrecht",
    label: "Erstberatung Unterhalt",
    apiUri: "/alimonies",
    formSteps: formStepsAlimony,
    formService: FormServiceAlimony,
    summaryStatus: APPLICATION_STATUS_SUMMARY_ALIMONY,
    productCategory: productCategoryData.familyLaw,
    productIcon: iconAlimony,
  },
  civilServiceLaw: {
    urlPart: "verwaltungsrecht/beamtenrecht",
    urlPartBase: "beamtenrecht",
    urlPartShort: "beamtenrecht",
    urlPartProductCategory: "verwaltungsrecht",
    label: "Beamtenrecht",
    apiUri: "/civil_service_laws",
    formSteps: formStepsCivilServiceLaw,
    formService: FormServiceCivilServiceLaw,
    summaryStatus: APPLICATION_STATUS_SUMMARY_CIVIL_SERVICE_LAW,
    productCategory: productCategoryData.administrativeLaw,
    productIcon: iconCivilServiceLaw,
  },
  dataLeakContract: {
    urlPart: "vertragsrecht/datenweitergabe",
    urlPartBase: "vertragsrecht",
    urlPartShort: "datenweitergabe",
    urlPartProductCategory: "vertragsrecht",
    label: "Datenweitergabe",
    apiUri: "/data_leak_contracts",
    formSteps: formStepsDataLeakContract,
    formService: FormServiceDataLeakContract,
    summaryStatus: APPLICATION_STATUS_SUMMARY_CONTRACT,
    productCategory: productCategoryData.contractLaw,
    productIcon: iconDataLeakContract,
  },
  divorce: {
    urlPart: "familienrecht/scheidung",
    urlPartBase: "familienrecht",
    urlPartShort: "scheidung",
    urlPartProductCategory: "familienrecht",
    label: "Scheidung",
    apiUri: "/divorces",
    formSteps: formStepsDivorce,
    formService: FormServiceDivorce,
    summaryStatus: APPLICATION_STATUS_SUMMARY_DIVORCE,
    productCategory: productCategoryData.familyLaw,
    productIcon: iconDivorce,
  },
  death: {
    urlPart: "familienrecht/todesfall",
    urlPartBase: "familienrecht/todesfall",
    urlPartShort: "todesfall",
    urlPartProductCategory: "familienrecht",
    label: "Erstberatung Erbrecht/Todesfall",
    apiUri: "/deaths",
    formSteps: formStepsDeath,
    formService: FormServiceDeath,
    summaryStatus: APPLICATION_STATUS_SUMMARY_DEATH,
    productCategory: productCategoryData.familyLaw,
    productIcon: iconDeath,
  },
  employmentContract: {
    urlPart: "arbeitsrecht/arbeit",
    urlPartBase: "arbeitsrecht",
    urlPartShort: "arbeitsvertrag",
    urlPartProductCategory: "arbeitsrecht",
    label: "Allgemeines Arbeitsrecht",
    apiUri: "/employment_contracts",
    formSteps: formStepsEmploymentContract,
    formService: FormServiceEmploymentContract,
    summaryStatus: APPLICATION_STATUS_SUMMARY_EMPLOYMENT_CONTRACT,
    productCategory: productCategoryData.employmentLaw,
    productIcon: iconEmploymentContract,
  },
  eventContract: {
    urlPart: "vertragsrecht/veranstaltung",
    urlPartBase: "vertragsrecht",
    urlPartShort: "veranstaltung",
    urlPartProductCategory: "vertragsrecht",
    label: "Veranstaltung",
    apiUri: "/event_contracts",
    formSteps: formStepsContractLaw,
    formService: FormServiceContractLaw,
    summaryStatus: APPLICATION_STATUS_SUMMARY_CONTRACT,
    productCategory: productCategoryData.contractLaw,
    productIcon: iconContract,
  },
  familyLawCase: {
    urlPart: "familienrecht/erstberatung",
    urlPartBase: "familienrecht",
    urlPartShort: "erstberatung",
    urlPartProductCategory: "familienrecht",
    label: "Erstberatung Familienrecht",
    apiUri: "/family_law_cases",
    formSteps: formStepsFamily,
    formService: FormServiceFamily,
    summaryStatus: 1000,
    productCategory: productCategoryData.familyLaw,
    productIcon: iconFamily,
  },
  generalCivilLaw: {
    urlPart: "zivilrecht/allgemeines-zivilrecht",
    urlPartBase: "allgemeines-zivilrecht",
    urlPartShort: "allgemeines-zivilrecht",
    urlPartProductCategory: "zivilrecht",
    label: "Allgemeines Zivilrecht",
    apiUri: "/general_civil_laws",
    formSteps: formStepsGeneralCivilLaw,
    formService: FormServiceGeneralCivilLaw,
    summaryStatus: APPLICATION_STATUS_SUMMARY_GENERAL_CIVIL_LAW,
    productCategory: productCategoryData.civilLaw,
    productIcon: iconCourtScales,
  },
  membershipContract: {
    urlPart: "vertragsrecht/mitgliedschaft",
    urlPartBase: "vertragsrecht",
    urlPartShort: "mitgliedschaft",
    urlPartProductCategory: "vertragsrecht",
    label: "Mitgliedschaft",
    apiUri: "/membership_contracts",
    formSteps: formStepsContractLaw,
    formService: FormServiceContractLaw,
    summaryStatus: APPLICATION_STATUS_SUMMARY_CONTRACT,
    productCategory: productCategoryData.contractLaw,
    productIcon: iconContract,
  },
  otherContract: {
    urlPart: "vertragsrecht/sonstige",
    urlPartBase: "vertragsrecht",
    urlPartShort: "sonstige",
    urlPartProductCategory: "vertragsrecht",
    label: "Sonstiger Vertrag",
    apiUri: "/other_contracts",
    formSteps: formStepsContractLaw,
    formService: FormServiceContractLaw,
    summaryStatus: APPLICATION_STATUS_SUMMARY_CONTRACT,
    productCategory: productCategoryData.contractLaw,
    productIcon: iconContract,
  },
  pendingCase: {
    urlPart: "neuer-fall/fallanlage",
    urlPartBase: "neuer-fall",
    urlPartShort: "fallanlage",
    urlPartProductCategory: "neuer Fall",
    label: "Fallschilderung erforderlich",
    apiUri: "/pending_cases",
    formSteps: formStepsGeneralCivilLaw,
    formService: FormServiceGeneralCivilLaw,
    summaryStatus: APPLICATION_STATUS_SUMMARY_GENERAL_CIVIL_LAW,
    productCategory: productCategoryData.none,
    productIcon: iconContract,
  },
  processCostSupport: {
    apiUri: "/process_cost_supports",
    urlPart: "scheidung",
    urlPartProductCategory: "familienrecht",
    urlPartShort: "scheidung",
    formSteps: formStepsProcessCostSupport,
    formService: FormServiceProcessCostSupport,
    summaryStatus: APPLICATION_STATUS_SUMMARY_PCS,
  },
  providerContract: {
    urlPart: "vertragsrecht/versorger",
    urlPartBase: "vertragsrecht",
    urlPartShort: "versorger",
    urlPartProductCategory: "vertragsrecht",
    label: "Versorgervertrag",
    apiUri: "/provider_contracts",
    formSteps: formStepsContractLaw,
    formService: FormServiceContractLaw,
    summaryStatus: APPLICATION_STATUS_SUMMARY_CONTRACT,
    productCategory: productCategoryData.contractLaw,
    productIcon: iconContract,
  },
  purchaseContract: {
    urlPart: "vertragsrecht/kauf",
    urlPartBase: "vertragsrecht",
    urlPartShort: "kauf",
    urlPartProductCategory: "vertragsrecht",
    label: "Kaufvertrag",
    apiUri: "/purchase_contracts",
    formSteps: formStepsContractLaw,
    formService: FormServiceContractLaw,
    summaryStatus: APPLICATION_STATUS_SUMMARY_CONTRACT,
    productCategory: productCategoryData.contractLaw,
    productIcon: iconContract,
  },
  rentalContract: {
    urlPart: "mietrecht/miete",
    urlPartBase: "mietrecht",
    urlPartShort: "miete",
    urlPartProductCategory: "mietrecht",
    label: "Mietvertrag",
    apiUri: "/rental_contracts",
    formSteps: formStepsRentalContract,
    formService: FormServiceRentalContract,
    summaryStatus: APPLICATION_STATUS_SUMMARY_CONTRACT,
    productCategory: productCategoryData.tenancyLaw,
    productIcon: iconTenancyLaw,
  },
  serviceContract: {
    urlPart: "vertragsrecht/dienstleistung",
    urlPartBase: "vertragsrecht",
    urlPartShort: "dienstleistung",
    urlPartProductCategory: "vertragsrecht",
    label: "Dienstleistungsvertrag",
    apiUri: "/service_contracts",
    formSteps: formStepsContractLaw,
    formService: FormServiceContractLaw,
    summaryStatus: APPLICATION_STATUS_SUMMARY_CONTRACT,
    productCategory: productCategoryData.contractLaw,
    productIcon: iconContract,
  },
  settlement: {
    urlPart: "arbeitsrecht/kuendigung",
    urlPartBase: "kuendigung",
    urlPartShort: "kuendigung",
    urlPartProductCategory: "arbeitsrecht",
    label: "Kündigungsschutzklage",
    apiUri: "/settlements",
    formSteps: formStepsSettlement,
    formService: FormServiceSettlement,
    summaryStatus: APPLICATION_STATUS_SUMMARY_SETTLEMENT,
    productCategory: productCategoryData.employmentLaw,
    productIcon: iconSettlement,
  },
  shortTimeWork: {
    urlPartShort: "kurzarbeit",
    urlPartProductCategory: "arbeitsrecht",
    label: "Erstberatung Kurzarbeit",
    apiUri: "/short_time_works",
    productCategory: productCategoryData.employmentLaw,
    productIcon: iconShortTimeWork,
  },
  subscriptionContract: {
    urlPart: "vertragsrecht/abonnement",
    urlPartBase: "vertragsrecht",
    urlPartShort: "abonnement",
    urlPartProductCategory: "vertragsrecht",
    label: "Abonnement",
    apiUri: "/subscription_contracts",
    formSteps: formStepsContractLaw,
    formService: FormServiceContractLaw,
    summaryStatus: APPLICATION_STATUS_SUMMARY_CONTRACT,
    productCategory: productCategoryData.contractLaw,
    productIcon: iconContract,
  },
  traffic: {
    urlPart: "verkehrsrecht/ordnungswidrigkeit",
    urlPartBase: "ordnungswidrigkeit",
    urlPartShort: "ordnungswidrigkeit",
    urlPartProductCategory: "verkehrsrecht",
    label: "Ordnungswidrigkeit",
    apiUri: "/traffic",
    formSteps: formStepsTraffic,
    formService: FormServiceTraffic,
    summaryStatus: APPLICATION_STATUS_SUMMARY_TRAFFIC,
    productCategory: productCategoryData.trafficLaw,
    productIcon: iconTraffic,
  },
  trafficAccident: {
    urlPart: "verkehrsrecht/verkehrsunfall",
    urlPartBase: "verkehrsunfall",
    urlPartShort: "verkehrsunfall",
    urlPartProductCategory: "verkehrsrecht",
    label: "Verkehrsunfall",
    apiUri: "/traffic_accidents",
    formSteps: formStepsTrafficAccident,
    formService: FormServiceTrafficAccident,
    summaryStatus: APPLICATION_STATUS_SUMMARY_TRAFFIC_ACCIDENT,
    productCategory: productCategoryData.trafficLaw,
    productIcon: iconTrafficAccident,
  },
  travelContract: {
    urlPart: "vertragsrecht/reise",
    urlPartBase: "vertragsrecht",
    urlPartShort: "reise",
    urlPartProductCategory: "vertragsrecht",
    label: "Reisevertrag",
    apiUri: "/travel_contracts",
    formSteps: formStepsContractLaw,
    formService: FormServiceContractLaw,
    summaryStatus: APPLICATION_STATUS_SUMMARY_CONTRACT,
    productCategory: productCategoryData.contractLaw,
    productIcon: iconContract,
  },
  vehicleContract: {
    urlPart: "vertragsrecht/fahrzeugkauf",
    urlPartBase: "vertragsrecht",
    urlPartShort: "fahrzeugkauf",
    urlPartProductCategory: "vertragsrecht",
    label: "Fahrzeugkaufvertrag",
    apiUri: "/vehicle_contracts",
    formSteps: formStepsContractLaw,
    formService: FormServiceContractLaw,
    summaryStatus: APPLICATION_STATUS_SUMMARY_CONTRACT,
    productCategory: productCategoryData.contractLaw,
    productIcon: iconContract,
  },
};

export const getProductData = (productClassName: AbstractCase["name"], path: string, defaultValue = "") => {
  return _get(productData, `${productClassName}.${path}`, defaultValue);
};

export const getProductNameBy = (path: string, value: string): AbstractCase["name"] => {
  return _findKey(productData, [path, value]) as AbstractCase["name"];
};

export const getCaseLink = (product: Pick<AbstractCase, "name" | "id">): string => {
  if (product.name === "processCostSupport") {
    // @ts-ignore
    return getCaseLink(product.divorce) + "/vkh-assistent";
  }

  if (product.name === "pendingCase") {
    return `/servicewelt/neuer-fall/${product.id}/fall-schildern`;
  }

  return "/servicewelt/" + getProductData(product.name, "urlPartProductCategory") + "/" + getProductData(product.name, "urlPartShort") + "/" + product.id;
};

export const getCaseSendMessageLink = (product: Pick<AbstractCase, "name" | "id">) => {
  return "/servicewelt/nachrichten/neu?product=" + product.name + "&productId=" + product.id;
};

export const getCaseDocumentUploadLink = (product: Pick<AbstractCase, "name" | "id">) => {
  return getCaseSendMessageLink(product) + "&documentUpload=true";
};

export const isInsurerPartnered = (product: AbstractCase) => {
  return isPartnerIriOneOf(product.acquisitionPartner, [
    PARTNER_IRIS.ADVOCARD,
    PARTNER_IRIS.ARAG,
    PARTNER_IRIS.AUXILIA,
    PARTNER_IRIS.HUK_COBURG,
    PARTNER_IRIS.NRV,
    PARTNER_IRIS.LVM,
    PARTNER_IRIS.DEURAG,
  ]);
};
